/**
 * LastRunCell — three-mode cell summarizing a connector's most recent run.
 *
 * Modes:
 *   success  — "<rel> ago · <rows> rows · <dur>"
 *   failure  — "<rel> ago · <error_code>" (red)
 *   running  — "started <rel> ago · <rows> rows so far" (amber)
 *   never    — "—"
 */
import React from 'react';
import type { ConnectorExecution, ExecutionStatus } from '@/types/connectors';
import { formatDuration } from '@/utils/formatDuration';

export interface LastRunCellProps {
  execution: ConnectorExecution | null | undefined;
}

const FAILURE_STATUSES: ReadonlySet<ExecutionStatus> = new Set([
  'failed',
  'partial',
  'cancelled',
  'timeout',
]);

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

export const LastRunCell: React.FC<LastRunCellProps> = ({ execution }) => {
  if (!execution) {
    return <span data-mode="never">—</span>;
  }

  if (execution.status === 'running' || execution.status === 'pending') {
    return (
      <span data-mode="running" className="text-warning">
        started {relativeTime(execution.started_at)} ·{' '}
        {formatNumber(execution.records_processed ?? 0)} rows so far
      </span>
    );
  }

  if (FAILURE_STATUSES.has(execution.status)) {
    const when = relativeTime(execution.completed_at ?? execution.started_at);
    const code = execution.error_message ?? execution.status;
    return (
      <span data-mode="failure" className="text-destructive">
        {when} · {code}
      </span>
    );
  }

  // success / skipped — treat as success-like
  const when = relativeTime(execution.completed_at);
  const rows = formatNumber(execution.records_processed ?? 0);
  const dur = formatDuration(execution.duration_seconds, { emptyText: '', zeroIsEmpty: true });
  return (
    <span data-mode="success">
      {when} · {rows} rows{dur ? ` · ${dur}` : ''}
    </span>
  );
};
